"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.THEME_FONTS_MARKER_END = exports.THEME_FONTS_MARKER_START = exports.DENSITY_SCALE = void 0;
exports.hexToHslChannels = hexToHslChannels;
exports.checkThemeTokens = checkThemeTokens;
exports.renderThemeCss = renderThemeCss;
exports.googleFontsHref = googleFontsHref;
exports.themeFontLinksHtml = themeFontLinksHtml;
exports.parseThemeCssHeader = parseThemeCssHeader;
const types_1 = require("./types");
// Theme tokens → CSS. The output lands in the app as frontend/src/dunea/theme.css
// (a platform file: apply_theme writes it, the agent never edits it).
//
// How it maps onto the landing template's styling:
//   - The template is shadcn + Tailwind v3: tailwind.config.js maps every colour
//     to `hsl(var(--background))` etc. and index.css sets those variables as
//     "H S% L%" channels. theme.css sets the SAME variables from the theme's
//     palette, so every existing bg-background / text-foreground / bg-primary /
//     border-border class follows the theme with no config change.
//   - It also exposes the theme's own tokens as --dunea-* variables (hex colours,
//     font stacks, spacing, type steps) for Tailwind arbitrary values such as
//     `bg-[var(--dunea-color-accent)]` or `text-[length:var(--dunea-text-5xl)]`.
//   - Specificity: theme.css is @imported at the top of index.css, so it comes
//     BEFORE index.css's own `:root {}` / `.dark {}` blocks. Its selectors are
//     `html:root` (0,1,1) and `html.dark` (0,1,1) so they win over the
//     template's `:root` (0,1,0) and `.dark` (0,1,0) regardless of order.
//
// Safety: every value interpolated into CSS is re-checked here (hex regex,
// font allowlist, bounded numbers, enum) even though manifests are validated
// with themeManifestSchema upstream. Anything else throws, so a malformed
// marketplace manifest can never inject CSS.
const HEX = /^#[0-9a-fA-F]{6}$/;
const THEME_ID = /^[a-z][a-z0-9-]{2,40}$/;
const SEMVER = /^\d+\.\d+\.\d+$/;
const FONT_SET = new Set(types_1.THEME_FONT_FAMILIES);
const SERIF_FONTS = new Set([
    'Instrument Serif',
    'Playfair Display',
    'Cormorant Garamond',
    'Fraunces',
    'DM Serif Display',
    'Libre Baskerville',
    'Lora',
    'Merriweather',
    'EB Garamond',
]);
const MONO_FONTS = new Set(['JetBrains Mono', 'Space Mono', 'Geist Mono']);
/**
 * Google Fonts css2 axis specs. css2 rejects the WHOLE request if any family is
 * asked for a weight it doesn't have, so single-weight and static families get
 * exactly what they ship.
 */
const SANS_AXES = 'wght@400;500;600;700';
const SERIF_AXES = 'ital,wght@0,400;0,500;0,600;0,700;1,400;1,500';
const FONT_AXES = {
    Lato: 'wght@300;400;700;900',
    'IBM Plex Sans': 'ital,wght@0,400;0,500;0,600;0,700;1,400',
    'Instrument Serif': 'ital@0;1',
    'DM Serif Display': 'ital@0;1',
    'Libre Baskerville': 'ital,wght@0,400;0,700;1,400',
    Merriweather: 'ital,wght@0,400;0,700;1,400',
    'Space Mono': 'ital,wght@0,400;0,700;1,400',
    'JetBrains Mono': 'ital,wght@0,400;0,500;0,700;1,400',
};
exports.DENSITY_SCALE = {
    compact: { unit: 0.875, section: 4, gutter: 1 },
    comfortable: { unit: 1, section: 6, gutter: 1.5 },
    airy: { unit: 1.25, section: 9, gutter: 2 },
};
const SPACE_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6];
const TYPE_STEPS = [
    ['xs', -2],
    ['sm', -1],
    ['base', 0],
    ['lg', 1],
    ['xl', 2],
    ['2xl', 3],
    ['3xl', 4],
    ['4xl', 5],
    ['5xl', 6],
];
function fail(msg) {
    throw new Error(`invalid theme tokens: ${msg}`);
}
function hex(v, path) {
    if (typeof v !== 'string' || !HEX.test(v)) {
        fail(`${path} must be #rrggbb`);
    }
    return v.toLowerCase();
}
function font(v, path) {
    if (typeof v !== 'string' || !FONT_SET.has(v)) {
        fail(`${path} must be one of THEME_FONTS`);
    }
    return v;
}
function num(n) {
    return String(Math.round(n * 1000) / 1000);
}
/** "#rrggbb" → shadcn channel triplet "H S% L%". */
function hexToHslChannels(value) {
    const h6 = hex(value, 'colour');
    const r = parseInt(h6.slice(1, 3), 16) / 255;
    const g = parseInt(h6.slice(3, 5), 16) / 255;
    const b = parseInt(h6.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    let h = 0;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    if (d !== 0) {
        if (max === r) {
            h = ((g - b) / d) % 6;
        }
        else if (max === g) {
            h = (b - r) / d + 2;
        }
        else {
            h = (r - g) / d + 4;
        }
        h = (h * 60 + 360) % 360;
    }
    return `${num(h)} ${num(s * 100)}% ${num(l * 100)}%`;
}
function mix(a, b, t) {
    const ch = (x, i) => parseInt(x.slice(1 + i * 2, 3 + i * 2), 16);
    const out = [0, 1, 2].map((i) => Math.round(ch(a, i) + (ch(b, i) - ch(a, i)) * t)
        .toString(16)
        .padStart(2, '0'));
    return `#${out.join('')}`;
}
function fontStack(family) {
    const fallback = MONO_FONTS.has(family)
        ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
        : SERIF_FONTS.has(family)
            ? 'ui-serif, Georgia, serif'
            : 'ui-sans-serif, system-ui, sans-serif';
    return `"${family}", ${fallback}`;
}
/** Validated, normalised copy of the tokens (throws on anything off-contract). */
function checkThemeTokens(tokens) {
    const palette = (p, path) => {
        if (!p || typeof p !== 'object') {
            fail(`${path} missing`);
        }
        const out = {};
        for (const k of types_1.THEME_PALETTE_KEYS) {
            out[k] = hex(p[k], `${path}.${k}`);
        }
        return out;
    };
    if (!tokens || typeof tokens !== 'object') {
        fail('tokens missing');
    }
    const { radius, density, typeScale } = tokens;
    if (!Number.isInteger(radius) || radius < 0 || radius > 32) {
        fail('radius must be an integer 0–32');
    }
    if (!Object.prototype.hasOwnProperty.call(exports.DENSITY_SCALE, density)) {
        fail('density must be compact | comfortable | airy');
    }
    if (typeof typeScale !== 'number' || !Number.isFinite(typeScale) || typeScale < 1.1 || typeScale > 1.6) {
        fail('typeScale must be 1.1–1.6');
    }
    return {
        colors: palette(tokens.colors, 'colors'),
        darkColors: tokens.darkColors ? palette(tokens.darkColors, 'darkColors') : undefined,
        fonts: {
            heading: font(tokens.fonts?.heading, 'fonts.heading'),
            body: font(tokens.fonts?.body, 'fonts.body'),
            mono: tokens.fonts?.mono === undefined ? undefined : font(tokens.fonts.mono, 'fonts.mono'),
        },
        radius,
        density,
        typeScale,
    };
}
function paletteVars(p) {
    const c = (h) => hexToHslChannels(h);
    const lines = [];
    for (const k of types_1.THEME_PALETTE_KEYS) {
        const kebab = k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
        lines.push(`--dunea-color-${kebab}: ${p[k]};`, `--dunea-color-${kebab}-hsl: ${c(p[k])};`);
    }
    // shadcn tokens (tailwind.config.js reads these). `accent` in shadcn is the
    // subtle hover/selection tint, so it gets a soft wash of the theme accent;
    // the full-strength accent is --dunea-color-accent.
    lines.push('/* shadcn tokens: bg-background, text-foreground, bg-primary, border-border… */', `--background: ${c(p.background)};`, `--foreground: ${c(p.text)};`, `--card: ${c(p.surface)};`, `--card-foreground: ${c(p.text)};`, `--popover: ${c(p.surface)};`, `--popover-foreground: ${c(p.text)};`, `--primary: ${c(p.primary)};`, `--primary-foreground: ${c(p.primaryText)};`, `--secondary: ${c(p.surface)};`, `--secondary-foreground: ${c(p.text)};`, `--muted: ${c(p.surface)};`, `--muted-foreground: ${c(p.muted)};`, `--accent: ${c(mix(p.surface, p.accent, 0.18))};`, `--accent-foreground: ${c(p.text)};`, `--border: ${c(p.border)};`, `--input: ${c(p.border)};`, `--ring: ${c(p.primary)};`);
    return lines;
}
function scaleVars(t) {
    const d = exports.DENSITY_SCALE[t.density];
    const lines = [
        `--dunea-font-heading: ${fontStack(t.fonts.heading)};`,
        `--dunea-font-body: ${fontStack(t.fonts.body)};`,
        `--dunea-font-mono: ${t.fonts.mono ? fontStack(t.fonts.mono) : 'ui-monospace, SFMono-Regular, Menlo, monospace'};`,
        `--dunea-radius: ${t.radius}px;`,
        `--radius: ${num(t.radius / 16)}rem;`,
    ];
    SPACE_STEPS.forEach((s, i) => lines.push(`--dunea-space-${i + 1}: ${num(s * d.unit)}rem;`));
    lines.push(`--dunea-space-section: ${num(d.section)}rem;`, `--dunea-space-gutter: ${num(d.gutter)}rem;`);
    for (const [name, step] of TYPE_STEPS) {
        // Below base the ratio would shrink text to unreadable sizes on steep
        // scales (1.5^-2 = 7px), so xs/sm are fixed UI sizes.
        const size = step === -2 ? 0.75 : step === -1 ? 0.875 : Math.pow(t.typeScale, step);
        if (step < 3) {
            lines.push(`--dunea-text-${name}: ${num(size)}rem;`);
            continue;
        }
        // Display steps are fluid: full size on wide screens, never below ~55%.
        const max = Math.min(size, 8);
        const min = Math.max(Math.pow(t.typeScale, 2), max * 0.55);
        lines.push(`--dunea-text-${name}: clamp(${num(min)}rem, ${num(max * 0.4)}rem + ${num(max * 1.2)}vw, ${num(max)}rem);`);
    }
    lines.push(`--dunea-leading-heading: ${t.typeScale >= 1.4 ? '1.02' : t.typeScale >= 1.25 ? '1.1' : '1.2'};`);
    return lines;
}
function block(selector, lines, indent = '') {
    return `${indent}${selector} {\n${lines.map((l) => `${indent}  ${l}`).join('\n')}\n${indent}}`;
}
/**
 * Render a theme as CSS custom properties. `meta` adds the identifying header
 * comment (`/* dunea-theme: <id>@<version> *\/`) that studio reads to tell which
 * theme an app uses.
 */
function renderThemeCss(input, meta) {
    const t = checkThemeTokens(input);
    const parts = [];
    if (meta) {
        if (!THEME_ID.test(meta.id) || !SEMVER.test(meta.version)) {
            fail('theme id/version');
        }
        parts.push(`/* dunea-theme: ${meta.id}@${meta.version} */`);
    }
    parts.push('/* Platform file written by apply_theme. Do not edit: apply another theme to change it. */', block('html:root', [...paletteVars(t.colors), ...scaleVars(t)]));
    if (t.darkColors) {
        const dark = paletteVars(t.darkColors);
        parts.push(block('html.dark', dark), '/* Follows the OS setting only when the app opts in: data-color-scheme="auto" on the html element. */', `@media (prefers-color-scheme: dark) {\n${block('html[data-color-scheme="auto"]:not(.light)', dark, '  ')}\n}`);
    }
    parts.push('body {\n  font-family: var(--dunea-font-body);\n}', 'h1, h2, h3, h4, h5, h6 {\n  font-family: var(--dunea-font-heading);\n  line-height: var(--dunea-leading-heading);\n}', 'code, kbd, pre, samp {\n  font-family: var(--dunea-font-mono);\n}', '.font-heading {\n  font-family: var(--dunea-font-heading);\n}', '.font-body {\n  font-family: var(--dunea-font-body);\n}');
    return `${parts.join('\n\n')}\n`;
}
/** The Google Fonts stylesheet URL for the theme's families (THEME_FONTS only, display=swap). */
function googleFontsHref(fonts) {
    const families = [fonts.heading, fonts.body, fonts.mono]
        .filter((f) => typeof f === 'string')
        .map((f, i) => font(f, `fonts[${i}]`));
    const unique = [...new Set(families)];
    const params = unique.map((f) => {
        const axes = FONT_AXES[f] ?? (SERIF_FONTS.has(f) ? SERIF_AXES : SANS_AXES);
        return `family=${encodeURIComponent(f).replace(/%20/g, '+')}:${axes}`;
    });
    return `https://fonts.googleapis.com/css2?${params.join('&')}&display=swap`;
}
exports.THEME_FONTS_MARKER_START = '<!-- dunea-theme-fonts -->';
exports.THEME_FONTS_MARKER_END = '<!-- /dunea-theme-fonts -->';
/** The <head> block apply_theme keeps in index.html (replaced on re-apply). */
function themeFontLinksHtml(fonts) {
    const href = googleFontsHref(fonts).replace(/&/g, '&amp;');
    return [
        exports.THEME_FONTS_MARKER_START,
        '<link rel="preconnect" href="https://fonts.googleapis.com" />',
        '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />',
        `<link rel="stylesheet" href="${href}" />`,
        exports.THEME_FONTS_MARKER_END,
    ].join('\n    ');
}
/** Reads the `/* dunea-theme: id@version *\/` header back out of a theme.css. */
function parseThemeCssHeader(css) {
    const m = /^\s*\/\* dunea-theme: ([a-z][a-z0-9-]{2,40})@(\d+\.\d+\.\d+) \*\//.exec(css);
    return m ? { id: m[1], version: m[2] } : null;
}
